import type { Expression } from '../../../../expression'
import * as expressionNode from '../../../../node'
import type { Fn } from '../../../fn'
import type { FnHelper } from '../../../helper'
import * as fnNode from '../../../node'
import { UnsupportedNodeError } from '../common-errors'

export function usingExpressionList<
  TFn extends Fn,
  TArgs extends readonly expressionNode.Node[],
  TStash,
>({
  emptyValue,
  finalizeArgs,
  getArgExpressions,
  handleValue,
  nodeClass,
  setupStash,
  treatSingleArgAs,
}: usingExpressionList.Params<
  TFn,
  TArgs,
  TStash
>): FnHelper<TFn>['createNode'] {
  /**
   * Return type is based on type parameter, which means TypeScript will never
   * accept anything as a valid return value. So we must use casting, and this
   * makes casting easier
   */
  type ReturnNode = ReturnType<FnHelper<TFn>['createNode']>
  return (fn, context): ReturnNode => {
    const stash = setupStash(fn, context)
    const expressions = getArgExpressions(fn, stash, context)

    // TODO: explain the below logic

    /** These cannot be resolved without querying for data */
    const argNodes: expressionNode.Node[] = []
    for (const expression of expressions) {
      // Simplifies each expression...
      const node = expressionNode.from(expression, context)
      if (expressionNode.ForValue.isForValue(node)) {
        const result = handleValue(node, stash, context)
        if (expressionNode.Node.isNode(result)) {
          argNodes.push(result)
        } else if (result !== undefined) {
          if (result.break) {
            break
          }
          return (expressionNode.Node.isNode(result.return)
            ? result.return
            : new expressionNode.ForValue(
                result.return,
              )) satisfies expressionNode.Node as ReturnNode
        }
      } else if (fnNode.Node.isNode(node)) {
        // Otherwise, sets it aside to use in the simplified expression
        argNodes.push(node)
      } else {
        throw new UnsupportedNodeError()
      }
    }
    const finalArgNodes = finalizeArgs(
      // Classic problem with TypeScript's deferred evaluation of type
      // parameters
      argNodes satisfies expressionNode.Node[] as unknown as TArgs,
      stash,
      context,
    )
    /**
     * This takes into account {@link treatSingleArgAs}
     */
    let effectiveNumArgs = finalArgNodes.length
    if (effectiveNumArgs === 1) {
      // Gets the desired treatment for the single arg
      const singleArgTreatment: usingExpressionList.SingleArgTreatment =
        (typeof treatSingleArgAs === 'function'
          ? treatSingleArgAs(finalArgNodes[0])
          : treatSingleArgAs) ?? 'result'
      if (singleArgTreatment === 'empty') {
        // If we should consider it empty, treat the list as if it has 0 items
        effectiveNumArgs = 0
      } else if (singleArgTreatment === 'arg') {
        // If we should keep it as an arg, treat the list as if it has anything
        // other than 0 or 1 items
        effectiveNumArgs = 2
      }
    }
    switch (effectiveNumArgs) {
      case 0:
        return new expressionNode.ForValue(
          emptyValue,
        ) satisfies expressionNode.Node as expressionNode.Node as ReturnNode
      case 1:
        return finalArgNodes[0] satisfies expressionNode.Node as ReturnNode
      default:
        return new nodeClass(
          finalArgNodes,
        ) satisfies expressionNode.Node as expressionNode.Node as ReturnNode
    }
  }
}
export namespace usingExpressionList {
  export interface Params<
    TFn extends Fn,
    TArgs extends
      readonly expressionNode.Node[] = readonly expressionNode.Node[],
    TStash = unknown,
  > {
    /**
     * Initializes the stash which can be used in later callbacks
     *
     * If not needed, simply put `() => undefined`
     */
    setupStash: (fn: TFn, context: expressionNode.Context) => TStash
    /**
     * Gets the list of expressions to convert into nodes and then process
     */
    getArgExpressions: (
      fn: TFn,
      stash: TStash,
      context: expressionNode.Context,
    ) => readonly Expression[]
    /**
     * Modifies the final list of args, if needed
     *
     * If not needed, simply put `arg => args`. This ensures you get type
     * errors if your {@link nodeClass} expects a different type of args
     */
    finalizeArgs: (
      args: TArgs,
      stash: TStash,
      context: expressionNode.Context,
    ) => TArgs
    /**
     * Handles an arg that was converted into an {@link expressionNode.ForValue}
     *
     * If you don't want any special treatment for value nodes, simply put `node => node`
     *
     * This is generally used to either:
     * 1. Update the stash (any return type)
     * 1. Simply use the value expression like we would a function expression ({@link expressionNode.Node} return type)
     * 1. Short-circuit the process and return a final expression (`{ return: unknown }` return type)
     * 1. Short-circuit the process and return based on what has been processed so far (`{ break: true }` return type)
     */
    handleValue: (
      node: expressionNode.ForValue,
      stash: TStash,
      context: expressionNode.Context,
    ) =>
      | TArgs[number]
      | {
          /** @see {@link handleValue} */
          return?: unknown
          /** @see {@link handleValue} */
          break?: true
        }
      | void
    /**
     * An {@link expressionNode.ForValue} is returned with this value when the
     * final arg list is empty
     */
    emptyValue: unknown
    /**
     * Determines what happens when the final arg list has only one item. This
     * can be a callback that evaluates that one item to determine how it should
     * be treated. The possible treatments are:
     *
     * - `result` - return that node as the final node (e.g. if the result would
     *   be the same as this arg, such as '-')
     * - `empty` - treat it as if there are no args (e.g. if this operation
     *   requires at least two args, such as '=')
     * - `arg` - still create a node with a list of args, even though that list
     *   will only have one arg (e.g. if this operation does logic to every arg,
     *   such as '+' being used for summing)
     *
     * @default 'result'
     */
    treatSingleArgAs?:
      | SingleArgTreatment
      | ((arg: expressionNode.Node) => SingleArgTreatment)
    /**
     * An instance of this is returned when the final arg list has at least two
     * items
     */
    nodeClass: new (args: TArgs) => fnNode.Node
  }

  /**
   * `undefined` is always equivalent to the default specified by
   * {@link Params.treatSingleArgAs}
   */
  export type SingleArgTreatment = 'result' | 'empty' | 'arg' | undefined
}
